<template>
    <div class="vuetable" style="flex: 1" :id="id">
        <div class="vuetableWrapper" ref="wrapper" style="overflow: hidden">
            <div class="thead">
                <table style="width: 100%" ref="headTable">
                    <thead ref="thead">
                    <slot name="header"></slot>
                    </thead>
                </table>
            </div>
            <div class="tbody-container" ref="bodyWrapper" :class="{loading}">
                <table ref="bodyTable">
                    <tbody ref="tbody" style="overflow: hidden">
                    <template v-for="(row, index) in rows">
                        <slot name="row" :item="row"
                              :indexOnPage="index"
                              :index="(pagination.currentPage - 1)*pagination.perPage + index+1"></slot>
                    </template>
                    <template v-show="rows.length === 0">
                        <tr style="opacity: 0;">
                            <td>.</td>
                        </tr>
                    </template>
                    </tbody>
                </table>
            </div>
        </div>
        <doctu-footer :pagination="pagination"
                      :rowscount="rows.length"
                      :show-clear="showClear"
                      :show-eye="showEye"
                      :is-async="Boolean(paginationUri)"
                      ref="footer"
                      @input="updatePagination"
        >
            <template v-slot:info>
                <slot name="footerInfo"></slot>
            </template>
        </doctu-footer>
        <doctu-settings ref="settings"
                        :hidden="hidden"
        ></doctu-settings>
    </div>
</template>

<script>
import Vue from 'vue';
import DoctuTableRow from "./DoctuTableRow.vue";
import DoctuTableFooter from "./DoctuTableFooter.vue";
import DoctuTableSettingsDialog from "./DoctuTableSettingsDialog.vue";

export default {
    name: "DoctuTable",
    components: {
        'doctu-tr': DoctuTableRow,
        'doctu-footer': DoctuTableFooter,
        'doctu-settings': DoctuTableSettingsDialog,
    },
    props: {
        requestMethod: {},
        requestUri: {
            default: ''
        },
        paginationUri: {
            default: ''
        },
        pagesize: {
            default: 100
        },
        model: {
            default: null
        },
        lazycount: {
            type: Boolean,
            default: false
        },
        defaultFilter: {
            type: Object,
            default() {
                return {}
            }
        },
        showClear: {
            type: Boolean,
            default: true
        },
        showEye: {
            type: Boolean,
            default: true
        },
        id: {
            default: 'vtable'
        }
    },
    data() {
        let paginationType = 'full';
        if (this.lazycount) {
            paginationType = 'lazy';
        }
        return {
            rows: [],
            filter: null,
            sort: [],
            pagination: {
                currentPage: 1,
                totalPages: 1,
                total: 0,
                perPage: this.pagesize,
                paginationType: paginationType,
                links: {},
            },
            cellWidths: [],
            fields: [],
            hidden: [],
            // hiddenCells: [],

            request: null,
            requestPagination: null,
            loading: false,
        }
    },
    created() {
        this.loadTableState();
        // this.update();
    },
    mounted() {
        this.$refs.bodyWrapper.addEventListener('scroll', this.layoutScroll);
        window.addEventListener('resize', this.layoutReflow)
        this.$bus.listen('sidebar-toggle', () => {
            this.$nextTick().then(() => {
                for (var i = 0; i < 10; i++) {
                    setTimeout(() => {
                        this.layoutReflow();
                    }, i * 50);
                }
            })
        });
    },
    destroyed() {
        window.removeEventListener('resize', this.layoutReflow);
    },
    updated() {
        this.layoutReflow();
    },
    computed: {
        keyName: function () {
            return location.pathname + '@vuetable#' + this.id;
        },
    },
    methods: {
        updatePagination: function (pagination) {
            Vue.set(this, 'pagination', pagination);
            this.$refs.bodyWrapper.scrollTop = 0;
            this.update();
        },
        update: function () {
            let url = this.requestUri;
            if (this.pagination.currentPage > 1) {
                if (url.indexOf('?') >= 0) {
                    url = url + '&page=' + this.pagination.currentPage;
                } else {
                    url = url + '?page=' + this.pagination.currentPage;
                }
            }

            this.saveTableState();
            let payload = this.getRequestPayload();
            this.fetch(url, payload);
        },
        fetch: function (url, options) {
            if (this.request) {
                this.request.cancel();
            }

            this.loading = true;
            this.request = this.axios.CancelToken.source();

            let request = null;
            if (this.requestMethod) {
                request = this.requestMethod(this.axios, url, options, {cancelToken: this.request.token});
            } else {
                request = this.axios.post(url, options, {cancelToken: this.request.token});
            }

            this.$emit('updating')

            if (this.lazycount && this.paginationUri && options.page === 1) {
                if (this.requestPagination) {
                    this.requestPagination.cancel();
                }

                let tablePagination = this.$refs.footer.$refs.eagerPagination ?? this.$refs.footer.$refs.lazyPagination;
                if (tablePagination) {
                    tablePagination.buttonsLoading = true;
                }

                this.requestPagination = this.axios.CancelToken.source();
                let requestPagination = this.axios.post(this.paginationUri, options, {cancelToken: this.requestPagination.token});
                requestPagination.then(response => {
                    if (tablePagination) {
                        tablePagination.buttonsLoading = false;
                    }

                    let pagination = JSON.parse(JSON.stringify(this.$refs.footer.pagination));
                    pagination.total = response.data.total;
                    pagination.totalPages = response.data.pages;
                    pagination.paginationType = 'full';
                    Vue.set(this, 'pagination', pagination);
                }).catch((err) => {
                    if (!this.axios.isCancel(err)) {
                        if (tablePagination) {
                            tablePagination.buttonsLoading = false;
                        }
                        console.log(err);
                    }
                });
            }

            request.then((response) => {
                this.storeResponse(response.data);
                this.loading = false;
                this.$emit('updated')
                let pagination = this.$refs.footer.$refs.eagerPagination ?? this.$refs.footer.$refs.lazyPagination;
                if (pagination) {
                    pagination.disableButtons = false;
                }
            }).catch((err) => {
                if (this.axios.isCancel(err)) {
                    console.log('Previous request canceled, new request is send');
                } else {
                    this.loading = false;
                    // handle error
                }

                let pagination = this.$refs.footer.$refs.eagerPagination ?? this.$refs.footer.$refs.lazyPagination;
                if (pagination) {
                    pagination.disableButtons = false;
                }
            });
        },
        getRequestPayload: function () {
            return {
                filter: this.filter,
                order: this.sort,
                params: [],

                page: this.pagination.currentPage,
                limit: this.pagination.perPage,
                lazycount: this.pagination.paginationType === 'lazy' || Boolean(this.paginationUri),
            };
        },

        storeResponse: function (response) {
            if (this.model) {

                this.rows = response.data.map((item) => {
                    return new this.model(item);
                });
            } else {
                this.rows = response.data;
            }

            if (response.pagination) {
                this.pagination = response.pagination;
            }
            this.pagination.currentPage = response.meta.current_page;
            this.pagination.perPage = response.meta.per_page;

            if (!this.paginationUri) {
                this.pagination.totalPages = response.meta.last_page;
            }

            if (response.links) {
                this.pagination.links = response.links;
            }

            if (response.meta.total) {
                this.pagination.total = response.meta.total;
                this.pagination.paginationType = 'full';
            } else if (!this.paginationUri) {
                this.pagination.totalPages = response.meta.current_page;
                this.pagination.paginationType = 'lazy';
            }
        },

        getRows: function () {
            let rows = this.rows;
            return rows;
        },
        filterUpdate: function (payload) {
            let key = Object.keys(payload)[0];
            let filter = JSON.parse(JSON.stringify(this.filter));
            if (Object.values(payload)[0] === '') {
                Vue.delete(filter, key)
            } else {
                Object.assign(filter, payload);
            }
            Vue.set(this, 'filter', filter);
        },

        filterClear: function () {
            Vue.set(this, 'sort', []);
            Vue.set(this, 'filter', this.defaultFilter);
            this.$emit('clear-table-filter');
        },
        getFilter: function () {
            return this.filter;
        },
        sortUpdate: function (payload) {

            let sortItem = {};
            let val = this.sort.find(function (e) {
                return Object.keys(e).indexOf(payload.name) >= 0;
            });
            if (val) {
                val = val[payload.name];
            }
            if (!val) {
                sortItem[payload.name] = 'ASC';
            }
            if (val == 'ASC') {
                sortItem[payload.name] = 'DESC';
            }
            if (val == 'DESC') {
                sortItem = {};
            }
            Vue.set(this, 'sort', [sortItem]);
        },

        sortUpdateMulti: function (payload) {
            let sort = JSON.parse(JSON.stringify(this.sort));
            let sortItemKey = sort.findIndex(function (e) {
                if (e[payload.name]) {
                    return e
                }
            });
            if (sortItemKey >= 0) {
                let sortItem = JSON.parse(JSON.stringify(this.sort[sortItemKey]));
                let val = this.sort[sortItemKey][payload.name];
                if (!val) {
                    sortItem[payload.name] = 'ASC';
                }
                if (val == 'ASC') {
                    sortItem[payload.name] = 'DESC';
                }
                if (val == 'DESC') {
                    sortItem = {};
                }

                sort[sortItemKey] = sortItem;
            } else {
                let sortItem = {};
                sortItem[payload.name] = 'ASC';
                sort.push(sortItem);
            }

            Vue.set(this, 'sort', sort.filter(function (e) {
                return Object.keys(e).length;
            }));
        },

        saveTableState: function () {
            var data = {
                filter: this.filter,
                order: this.sort,
                hidden: this.hidden,
                page: this.pagination.currentPage,
            };
            localStorage.setItem(this.keyName, JSON.stringify(data));
        },

        loadTableState: function () {
            var data = JSON.parse(localStorage.getItem(this.keyName));
            if (data) {
                Vue.set(this, 'filter', data.filter || {});
                if (Array.isArray(data.order)) {
                    Vue.set(this, 'sort', data.order || []);
                } else {
                    this.sort = [];
                }

                Vue.set(this, 'hidden', data.hidden || []);
                this.currentPage = data.page || 1;
            } else {
                Vue.set(this, 'filter', {});
                Vue.set(this, 'sort', []);
                Vue.set(this, 'hidden', []);
                this.currentPage = 1;
            }
        },

        layoutResize: function () {

        },

        layoutReflow: function () {
            this.$refs.wrapper.style.paddingTop = this.$refs.headTable.offsetHeight + 'px';

            let headerCells = this.$refs.thead.querySelectorAll('tr:first-child > th');
            let bodyCells = this.$refs.tbody.querySelectorAll('tr:first-child > td');
            let elWidth = +this.$el.offsetWidth;

            let widthsSum = 0;
            let widthsCount = 0;

            let minwidthsSum = 0;
            let minwidthsCount = 0;

            let autowidthsSum = 0;
            let autowidthsCount = 0;
            for (let i = 0; i < headerCells.length; i++) {
                let headerCell = headerCells[i];

                if (headerCell.getAttribute('width')) {
                    widthsSum += parseInt(headerCell.getAttribute('width'));
                    widthsCount++;
                } else if (headerCell.getAttribute('minwidth')) {
                    minwidthsSum += parseInt(headerCell.getAttribute('minwidth'));
                    minwidthsCount++;
                } else {
                    autowidthsSum += 40;
                    autowidthsCount++;
                }
            }

            let cellWidths = [];
            if ((widthsCount + minwidthsCount + autowidthsCount) > 0) {
                let totalWidth = 0;
                for (let i = 0; i < headerCells.length; i++) {
                    let headerCell = headerCells[i];
                    let bodyCell = bodyCells[i];

                    let headerCellWidth = headerCell.getAttribute('width');
                    let cellWidth = 0;
                    if (headerCellWidth) {
                        cellWidth = headerCellWidth;
                    } else {
                        let headerMinwidthAttr = +headerCell.getAttribute('minwidth') || 40;
                        let calcWidth = (elWidth - widthsSum - 8) / (minwidthsCount + autowidthsCount);
                        cellWidth = Math.max(+calcWidth, +headerMinwidthAttr);
                    }
                    if (cellWidth) {
                        cellWidth = +cellWidth;
                        headerCell.style.width = '' + cellWidth + 'px';
                        if (bodyCell) {
                            bodyCell.style.width = '' + cellWidth + 'px';
                        }
                        totalWidth += cellWidth;
                    }

                    cellWidths[i] = cellWidth;
                }

                this.cellWidths = cellWidths;
                // console.log(totalWidth, this.$refs.wrapper.offsetWidth, this.$refs.wrapper);
                // todo если суммарная ширина столбцов меньше заданной ширины таблицы и кол-во столбцов больше 3ех - равномерно растягивать текущие столбцы
                this.$refs.headTable.style.width = (totalWidth - 8) + 'px';
                this.$refs.bodyWrapper.style.width = (this.$refs.wrapper.offsetWidth - 2) + 'px';
                this.$refs.bodyTable.style.width = (totalWidth - 4) + 'px';
                // $tbodyDummy.css('width', totalWidth - 8); // TODO check if empty result we need to scroll
            }
        },

        layoutScroll: function (evt) {
            let scrollVal = (this.$refs.bodyWrapper.scrollLeft);
            this.$refs.headTable.style.left = '-' + scrollVal + 'px';
        },

        pushField: function (element) {
            let elt = this.fields.find(function (e) {
                return e.name === element.name;
            });
            if (!elt) {
                this.fields.push(element);
            }
        },

        showTableSettingsDialog: function () {
            this.$refs.settings.show();
        },

        getFilteredObject: function (obj, keysArray) {
            return Object.fromEntries(
                Object.entries(obj).filter(item => {
                    return !keysArray.includes(item[0]);
                })
            );
        },

        setHidden: function (hidden) {
            let hiddenFilters = hidden.map(item => item.name);
            let hiddenSorts = hidden.map(item => {
                if (item.sort !== undefined) {
                    return item.sort;
                }
            });

            this.filter = this.getFilteredObject(this.filter, hiddenFilters);

            if (this.sort.length > 0) {
                this.sort = [
                    this.getFilteredObject(this.sort[0], hiddenSorts)
                ];
            }

            Vue.set(this, 'hidden', hiddenFilters);
        }
    },
    watch: {
        hidden: function () {
            this.saveTableState();
        },
        filter: function (newVal, oldVal) {
            if (JSON.stringify(newVal) != JSON.stringify(oldVal)) {
                this.pagination.currentPage = 1;
                this.update();
            }
        },
        sort: function (newVal, oldVal) {
            if (JSON.stringify(newVal) != JSON.stringify(oldVal)) {
                this.update()
            }
        },
        requestUri(){
            this.update();
        }
    }
}
</script>

<style lang="scss">
.vuetable {
    width: 100%;
    position: relative;
    overflow: hidden;

    & > .vuetableWrapper {
        width: 100%;
        border: 1px solid #D2D2D2;
        background-color: #f5f5f5;
        position: absolute;
        top: 0;
        bottom: 30px;

        & > .thead {
            width: 100%;
            position: absolute;
            top: 0;

            & > table {
                position: absolute;
                will-change: scroll-position;
                width: 100%;
                min-width: 500px;
                clear: both;
                margin: 0 auto;
                border: none;
                font-size: 13px;
                table-layout: fixed;
                z-index: 1;

                & > thead > tr {
                    & > th {
                        padding: 2px;
                        text-overflow: ellipsis;
                        min-width: 10px;
                        text-align: center;
                        vertical-align: middle;
                        background-color: #ebebeb;
                        user-select: none;
                        -o-user-select: none;
                        -moz-user-select: none;
                        -khtml-user-select: none;
                        -webkit-user-select: none;
                        /*overflow: hidden*/
                    }

                    & > th[order] {
                        cursor: pointer !important;
                        background-position: top 3px right 3px;
                        background-repeat: no-repeat
                    }

                    & > th[order][order][mouseover]:hover {
                        background-color: #e3e3e3;
                        background-image: url(data:image/png;base64,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)
                    }

                    & > th[order].orderASC {
                        background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAYAAAAICAYAAADaxo44AAAAL0lEQVQImWP8//8/AzKQ1NL9z8DAwMDEgAMwouuAARZ0I2CAdKMYJTR1sMrgNAoA0zENUIzEsRAAAAAASUVORK5CYII=)
                    }

                    & > th[order].orderASC[mouseover]:hover {
                        background-color: #e3e3e3 !important;
                        background-image: url(data:image/png;base64,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) !important
                    }

                    & > th[order].orderDESC {
                        background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAYAAAAICAYAAADaxo44AAAALElEQVQImWOU0NT5z4AFMGETZGBgYGD8/x+rBgYWGENSSxdFBQVGoRuJ0ygAgjkKVl7EnecAAAAASUVORK5CYII=)
                    }

                    & > th[order].orderDESC[mouseover]:hover {
                        background-color: #e3e3e3;
                        background-image: none !important
                    }

                    & > th input,
                    & > th select {
                        background-color: #fff;
                        border: 1px solid #ededed;
                        margin-left: 0;
                        margin-right: 0;
                        padding-top: 0;
                        padding-left: 1px;
                        font-size: 12px;
                        font-weight: 400
                    }

                    & > th input:not([type=checkbox]) {
                        font-size: 12px;
                        font-weight: 400;
                        display: block;
                        width: 100% !important;
                        min-width: 100% !important;
                        max-width: 100% !important
                    }

                    & > th input[type=checkbox] {
                        width: 23px;
                        height: 23px;
                        margin: 0 0 5px
                    }

                    & > th input[disabled] {
                        background-image: none !important;
                        background-color: transparent !important;
                        outline: 0 !important
                    }

                    & > th input.notempty,
                    & > th input.notempty:focus,
                    & > th select.notempty,
                    & > th select.notempty:focus {
                        background-color: #ff0 !important
                    }

                    & > th input.empty:hover:not([type=checkbox]):not(:focus) {
                        background-position: left center;
                        background-repeat: no-repeat;
                        background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAC8AAAALCAYAAADvGtSMAAABTUlEQVR4Xs3UsWoUURQG4O/cnc5iSRt9B99ATC3kUWyWoMVms3EJhBRWKQXbxMpW8gDp9wXs0hj1CeYeZSAsDDOoRXC+8szlzH/OcCesE/alM7UeKuaUrepC5JXaAshNY0oK9rV5i3uz8tyP8kR6g1O8NWGNdKbEJxzZuTFzIHOLa3zVExFpWAByvC4AMrPr9VsM9c3MeHjef3ej1sNu433cqeWzEq9wCTDQ2I4cCjhW64fth+zrn2kUc999A7CHd4CVe2Hu8eTDZo0zNlxD2drzQnGjtUMIL2VsTMDQgI3qApc4wB0grPIIz5T44nEk4h8+UfS3X3Al8qOsW3zAuRO3wmviqWqpouZ/Cz50gaEBnGvrdXc5w1zGptt4tRROJFj7ezH+twHBeLB+bexcOE4iqS1lRiCDggrWugFykafx3oQUf7aWuRD508T8AicGnMCccG/FAAAAAElFTkSuQmCC)
                    }

                    & > th input.empty:hover:not([type=checkbox]):not(:focus)::placeholder {
                        color: transparent
                    }

                    & > th select.empty:hover:not([type=checkbox]):not([placeholder]) {
                        background-position: left center;
                        background-repeat: no-repeat;
                        background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAC8AAAALCAYAAADvGtSMAAABTUlEQVR4Xs3UsWoUURQG4O/cnc5iSRt9B99ATC3kUWyWoMVms3EJhBRWKQXbxMpW8gDp9wXs0hj1CeYeZSAsDDOoRXC+8szlzH/OcCesE/alM7UeKuaUrepC5JXaAshNY0oK9rV5i3uz8tyP8kR6g1O8NWGNdKbEJxzZuTFzIHOLa3zVExFpWAByvC4AMrPr9VsM9c3MeHjef3ej1sNu433cqeWzEq9wCTDQ2I4cCjhW64fth+zrn2kUc999A7CHd4CVe2Hu8eTDZo0zNlxD2drzQnGjtUMIL2VsTMDQgI3qApc4wB0grPIIz5T44nEk4h8+UfS3X3Al8qOsW3zAuRO3wmviqWqpouZ/Cz50gaEBnGvrdXc5w1zGptt4tRROJFj7ezH+twHBeLB+bexcOE4iqS1lRiCDggrWugFykafx3oQUf7aWuRD508T8AicGnMCccG/FAAAAAElFTkSuQmCC)
                    }

                    & > th select.empty:not(:hover),
                    & > th select.notempty {
                        background-image: none
                    }

                    & > th input[type=date].empty::-webkit-datetime-edit-day-field,
                    & > th input[type=date].empty::-webkit-datetime-edit-month-field,
                    & > th input[type=date].empty::-webkit-datetime-edit-text,
                    & > th input[type=date].empty::-webkit-datetime-edit-year-field {
                        display: none;
                        color: transparent
                    }
                }
            }
        }
    }
}

.vuetable > .vuetableWrapper > .tbody-container {
    height: 100%;
    width: 100%;
    overflow-y: scroll;
    overflow-x: auto;
    position: relative;
    background-color: #f4f4f4;

    & > table {
        width: 100%;
        min-width: 500px;
        clear: both;
        margin: 0;
        border: none;
        font-size: 13px;
        table-layout: fixed;

        & > tbody,
        & > tfoot {
            -webkit-user-select: text;
            -moz-user-select: text
        }

        & > tbody > tr {

            &.dummy > td {
                height: 0;
                padding: 0;
                margin: 0
            }

            &.canceled * {
                text-decoration: line-through;
                color: #999
            }

            &.selected {
                background-color: #308eff4f !important
            }

            &.strong * {
                font-weight: 700
            }

            &:nth-child(2n) {
                background-color: rgba(0, 0, 0, .02)
            }

            &:nth-child(2n+1) {
                background-color: rgba(255, 255, 255, .5)
            }

            &.selected td {
                background-color: rgba(238, 247, 253, .15) !important
            }

            &.selected.hover:hover td {
                background-color: rgba(233, 242, 248, .15) !important
            }

            &.warning td {
                background-color: rgba(255, 255, 0, .15) !important
            }

            &.warning.selected td,
            &.warning.selected.hover:hover td {
                background-color: rgba(238, 247, 253, .15) !important
            }

            & > td {
                padding: 3px;
                overflow: hidden;
                text-overflow: ellipsis;
                min-width: 10px
            }

            & > td input:not([type]),
            & > td input[type=number],
            & > td input[type=text],
            & > td select {
                margin: 0;
                padding: 0;
                width: 100%
            }
          & > td .warning{
            background-color: rgba(255, 255, 0, .15) !important
          }
            & > td input[type=checkbox] {
                margin: 0;
                width: 13px;
                height: 13px;
                vertical-align: middle;
            }
        }

        & > tfoot > tr {
            & > td {
                padding: 3px;
                overflow: hidden;
                text-overflow: ellipsis
            }

            & > td input:not([type]),
            & > td input[type=number],
            & > td input[type=text],
            & > td select {
                margin: 0;
                padding: 0;
                width: 100%
            }
        }
    }
}

.vuetable .footer {
    clear: both;
    color: #333;
    font-size: 13px;
    position: absolute;
    width: 100%;
    bottom: 0;
    height: 20px;

    display: flex;
}

.vuetable .footer .info {
    /*position: absolute;*/
    /*left: 0;*/
    /*text-align: left*/
}

.vuetable .footer .center {
    flex: 1 1;
}

.vuetable .footer .pagination {
    /*position: absolute;*/
    /*right: 0;*/
    text-align: right;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    height: 24px;
    user-select: none;
}

.vuetable .footer .pagination a {
    display: inline-block;
    color: #333
}

.vuetable .footer .pagination button {
    min-width: 33px
}

.vuetable .footer .pagination select {
    width: 33px;
    height: 15px;
    position: relative;
    text-align: center;
    vertical-align: text-bottom;
}

.vuetable .footer .pagination select[disabled] {
    cursor: default;
    opacity: .5
}

.vuetable.loading {
    background-image: none !important
}

.vuetable.pointerEventsNone div.footer {
    pointer-events: initial !important
}

.vuetable.loading > .vuetableWrapper > .tbody-container, tr.loading {
    animation: stripes-animation 2s linear infinite;
    background-image: linear-gradient(45deg, transparent 25%, rgba(0, 0, 0, .1) 25%, rgba(0, 0, 0, .1) 50%, transparent 50%, transparent 75%, rgba(0, 0, 0, .1) 75%, rgba(0, 0, 0, .1)) !important;
    background-size: 40px 40px !important;
    opacity: 1 !important;
    background-repeat: repeat !important;
    background-color: inherit !important;
    cursor: progress !important
}

tr.loading.hover:hover {
    background-image: linear-gradient(45deg, #e4edf3 25%, #cdd5db 25%, #cdd5db 50%, #e4edf3 50%, #e4edf3 75%, #cdd5db 75%, #cdd5db) !important
}
</style>
